<template>
  <div
    class="py-4 px-4 h-100"
  >
    <template>
      <section
        class="mb-3"
      >
        <b-row>
          <b-col>
            <base-help-tooltip
              popover-placement="bottom"
            >
              <template v-slot:left>
                <div>
                  <h1 class="page-title">
                    Publisher Performance
                  </h1>
                </div>
              </template>
              <template v-slot:popover-body>
                <p>View a breakdown of the data by individual publishers.</p>
              </template>
            </base-help-tooltip>
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row>
          <b-col
            md="1"
            lg="1"
          >
            <b-dropdown
              id="publishers-overview-sort-button"
              class="mb-2"
              :text="'Sort By: ' + getSortByValue(sortBy)"
            >
              <b-dropdown-item
                v-for="(option, index) in sortByOptions"
                :key="index"
                @click="updateSortBy(option)"
              >
                {{ getSortByValue(option) }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
          <b-col
            md="11"
            lg="11"
          >
            <Filters
              @updateReport="updateReport"
              @setPageLoading="setPageLoading"
              @updateDates="updateDates"
              @areAllParametersPresent="areAllParametersPresent"
              @setCampaignPublishersList="setCampaignPublishersList"
            />
          </b-col>
        </b-row>
        <b-row
          v-if="isPageLoading"
        >
          <div
            class="loader mx-auto h-100"
          />
        </b-row>
        <b-row
          else
        >
          <b-alert
            v-if="!isPageLoading && !dataIsPresent"
            show
            variant="warning"
            class="w-50 mt-5 mx-auto text-center"
          >
            There's no data to show
          </b-alert>

          <b-col
            v-else
            class="col-xl-6 col-xxl-4 col-12"
            v-for="(publisher, index) in sortedPublishersList"
            :key="index"
          >
            <publisher-report-card
              :publisher="publisher"
              :has-scrolls="hasScrollsData"
            />
          </b-col>
        </b-row>
      </section>
    </template>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapState,
} from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';

import PublisherReportCard from '@/modules/reporting/components/PublisherReportCard';
import Filters from '@/modules/reporting/components/Filters.vue';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';
import fileHelper from '@/core/mixins/fileHelper';

import helper from '@/core/helper';

export default {
  name: 'PublisherPerformance',
  components: {
    BaseHelpTooltip,
    PublisherReportCard,
    Filters,
  },
  mixins: [
    fileHelper,
  ],
  data() {
    return {
      dataIsPresent: true,
      isPageLoading: true,
      campaignList: [],
      campaignPublishersList: [],
      campaignContentItems: [],
      contentList: [],
      creativeList: [],
      publishersStats: [],
      sortBy: 'ctr',
      selectedCampaignIds: [],
      selectedPublishersIds: [],
      selectedCreativesIds: [],
      selectedContentItemIds: [],
      dateRange: {
        start: -1,
        end: -1,
      },
      allParametersArePresent: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'order',
      'orders',
      'campaign',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_CAMPAIGN_MIN_START_DATE,
      campaignTypes.GET_CAMPAIGN_MAX_END_DATE,
      campaignTypes.GET_ORDERS_START_DATE,
      campaignTypes.GET_ORDERS_END_DATE,
    ]),
    sortedPublishersList() {
      return this.getSortedPublishersList(this.sortBy);
    },
    hasScrollsData() {
      return this.publishersStats.some((stats) => stats.scrolls != null);
    },
    sortByOptions() {
      const withScrolls = [
        'name',
        'impressions',
        'clicks',
        'ctr',
        'scrolls',
        'str',
      ];
      const noScrolls = [
        'name',
        'impressions',
        'clicks',
        'ctr',
      ];
      return this.hasScrollsData ? withScrolls : noScrolls;
    },
    fullPublishersDetails() {
      const publishersDetails = [];
      for (let index = 0; index < this.publishersStats.length; index++) {
        const publisherStats = this.publishersStats[index];
        const publisherInfo = this.campaignPublishersList.find((publisher) => publisherStats.publisherId === publisher.id);
        publishersDetails.push({
          publisherInfo,
          publisherStats,
        });
      }
      return publishersDetails;
    },
    startDateForReport() {
      return this.dateRange?.start == null || this.dateRange.start === -1
        ? this.GET_ORDERS_START_DATE : this.dateRange.start;
    },
    endDateForReport() {
      return this.dateRange?.end == null || this.dateRange.end === -1
        ? this.GET_ORDERS_END_DATE : this.dateRange.end;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_PUBLISHERS,
    ]),
    ...mapActions('reporting', [
      reportingTypes.FETCH_PUBLISHER_IDS_WITH_STATS,
      reportingTypes.FETCH_CAMPAIGN_PUBLISHER_STATS,
    ]),
    updateDates(startDate, endDate) {
      this.dateRange.start = startDate;
      this.dateRange.end = endDate;
      this.updateReport();
    },
    getSortedPublishersList(sortBy) {
      if (sortBy === 'name') {
        return this.fullPublishersDetails.sort((a, b) => a.publisherInfo.name.localeCompare(b.publisherInfo.name));
      }
      return this.fullPublishersDetails.sort((a, b) => b.publisherStats[sortBy] - a.publisherStats[sortBy]);
    },
    getSortByValue(label) {
      if (label === 'ctr' || label === 'str') return `${label.toUpperCase()}%`;
      return label.toUpperCase();
    },
    updateSortBy(sortBy) {
      this.sortBy = sortBy;
    },
    getPublisherStats() {
      const { advertiserId } = this.$route.params;

      const payload = {
        advertiserId,
        body: {
          fromDate: this.startDateForReport,
          toDate: this.endDateForReport,
          campaignIds: this.selectedCampaignIds,
          publisherIds: this.selectedPublishersIds,
          creativeIds: this.selectedCreativesIds,
          contentItemIds: this.selectedContentItemIds,
        },
      };

      this.FETCH_CAMPAIGN_PUBLISHER_STATS(payload)
        .then((publishersStats) => {
          this.publishersStats = publishersStats;
        });
    },
    updateReport: helper.debounce(function fetchData(data) {
      this.setPageLoading(true);
      if (this.allParametersArePresent) {
        this.selectedCampaignIds = data.campaignIds;
        this.selectedPublishersIds = data.publisherIds;
        this.selectedCreativesIds = data.creativeIds;
        this.selectedContentItemIds = data.contentItemIds;
        this.getPublisherStats();
        this.setPageLoading(false);
        this.dataIsPresent = true;
      } else {
        this.setPageLoading(false);
        this.dataIsPresent = false;
      }
    }, 1500),
    setPageLoading(value) {
      this.isPageLoading = value;
    },
    areAllParametersPresent(value) {
      this.allParametersArePresent = value;
    },
    setCampaignPublishersList(publishersList) {
      this.campaignPublishersList = publishersList;
    },
  },
  async created() {
    try {
      const { campaignId } = this.$route.params;
      this.campaignId = campaignId;
      this.updateDates(this.GET_CAMPAIGN_MIN_START_DATE, this.GET_CAMPAIGN_MAX_END_DATE);
    } catch (err) {
      this.$router.back();
    }
  },
};
</script>
